import { useState, useCallback } from 'react';
import { resumenCorporativoApi } from '../api/resumenCorporativoApi';
import { showErrorToast, showSuccessToast } from '../utils/errorHandler';

/**
 * Hook personalizado para gestión del resumen corporativo
 * Proporciona estado y funciones para obtener datos de resumen gerencial
 */
export const useResumenCorporativo = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resumen, setResumen] = useState(null);

  /**
   * Obtiene el resumen corporativo para una fecha específica
   */
  const fetchResumen = useCallback(async (fecha = null) => {
    setLoading(true);
    setError(null);

    try {
      const data = await resumenCorporativoApi.getResumen(fecha);
      setResumen(data);
      return data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al obtener resumen corporativo');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene el resumen por rango de fechas
   */
  const fetchResumenPorRango = useCallback(async (fechaInicio, fechaFin) => {
    setLoading(true);
    setError(null);

    try {
      const data = await resumenCorporativoApi.getResumenPorRango(fechaInicio, fechaFin);
      setResumen(data);
      showSuccessToast('Resumen obtenido exitosamente');
      return data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al obtener resumen por rango de fechas');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Actualiza el resumen (refetch)
   */
  const refetchResumen = useCallback(async () => {
    if (resumen?.fecha_consulta) {
      return await fetchResumen(resumen.fecha_consulta);
    } else {
      return await fetchResumen();
    }
  }, [fetchResumen, resumen]);

  return {
    // Estado
    loading,
    error,
    resumen,

    // Funciones
    fetchResumen,
    fetchResumenPorRango,
    refetchResumen,

    // Función para limpiar errores
    clearError: () => setError(null),
  };
};

export default useResumenCorporativo;